import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { ControlCenter } from "@/features/admin";
import { useChartTheme } from "@/features/charts/theme";
import { ChartTooltip } from "@/features/charts/tooltip";
import { useDateFnsLocale } from "@/features/utils/i18n";
import { ResponsiveLine } from "@nivo/line";
import { format as formatDate } from "date-fns";

/**
 * Traffic over the window: calls and chats as filled areas, and on top of them what went
 * wrong — conversations that ended in failure and tool incidents — so a spike of errors
 * is read against the traffic it happened in.
 */
export function TrafficChart({ data }: { data: ControlCenter }) {
  const { theme, tokens } = useChartTheme();
  const locale = useDateFnsLocale();
  const daily = data.bucket_ms >= 24 * 60 * 60 * 1000;
  const tickFormat = daily ? "d MMM" : data.hours > 24 ? "EEE HH'h'" : "HH:mm";

  const series = [
    { id: "Appels", key: "calls", color: tokens.series[0] },
    { id: "Chats", key: "chats", color: tokens.series[1] },
    { id: "Échecs", key: "failures", color: tokens.tone("red") },
    { id: "Incidents outils", key: "incidents", color: tokens.tone("yellow") },
  ] as const;

  const hasData = data.timeline.some(
    (p) => p.calls || p.chats || p.failures || p.incidents,
  );

  return (
    <Card className="h-full">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Trafic et incidents</CardTitle>
        <p className="text-xs text-muted-foreground">
          Conversations ouvertes par tranche de{" "}
          {bucketLabel(data.bucket_ms)}
        </p>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          {!hasData ? (
            <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
              Aucune conversation sur la période.
            </div>
          ) : (
            <ResponsiveLine
              data={series.map((s) => ({
                id: s.id,
                data: data.timeline.map((p) => ({
                  x: new Date(p.t),
                  y: p[s.key],
                })),
              }))}
              colors={series.map((s) => s.color)}
              margin={{ top: 12, right: 16, bottom: 36, left: 40 }}
              xScale={{ type: "time", useUTC: false }}
              yScale={{ type: "linear", min: 0, max: "auto" }}
              curve="monotoneX"
              lineWidth={2}
              enableArea
              areaOpacity={0.12}
              areaBaselineValue={0}
              pointSize={0}
              enableGridX={false}
              gridYValues={4}
              axisTop={null}
              axisRight={null}
              axisBottom={{
                tickSize: 0,
                tickPadding: 10,
                tickValues: 6,
                format: (v: Date) => formatDate(v, tickFormat, { locale }),
              }}
              axisLeft={{ tickSize: 0, tickPadding: 8, tickValues: 4 }}
              enableSlices="x"
              animate
              motionConfig="gentle"
              theme={theme}
              sliceTooltip={({ slice }) => (
                <ChartTooltip
                  title={
                    slice.points[0]
                      ? formatDate(
                          slice.points[0].data.x as Date,
                          daily ? "EEEE d MMM" : "EEEE d MMM, HH:mm",
                          { locale },
                        )
                      : null
                  }
                  rows={slice.points.map((point) => ({
                    key: point.id,
                    color: point.seriesColor,
                    label: point.seriesId,
                    value: String(point.data.y),
                  }))}
                />
              )}
            />
          )}
        </div>
        <ul className="mt-2 flex flex-wrap justify-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          {series.map((s) => (
            <li key={s.id} className="flex items-center gap-1.5">
              <span
                className="size-2 rounded-full"
                style={{ background: s.color }}
              />
              {s.id}
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

const bucketLabel = (ms: number): string => {
  const min = ms / 60_000;
  if (min < 60) return `${min} min`;
  const h = min / 60;
  if (h < 24) return `${h} h`;
  return `${h / 24} j`;
};
